import { ENDLESS_START, endlessStartSave } from '../../core/endlessStart';
import { clearSave, writeSave } from '../../core/storage';
import { RARITY_NAME } from '../../data/machines';
import { formatGold } from '../format';
import { hasGateApi, isEntitled, onGameEvent } from '../gameApi';
import { toast } from '../toast';
import { isMuted, setMuted } from '../audioPref';
import { maybeShowLore } from '../lore';
import { consumeSaveResetNotice } from '../saveNotice';
import type { ScreenCtx, ScreenMount } from '../types';
import { APP_VERSION, openDevNotes } from './devNotes';
import { IS_PREPROD } from '../channel';
import { icon } from '../icons';
import { dieArt } from '../diceArt';
import { openLeaderboard } from './leaderboard';

/** Icône du bouton de son (même convention que le HUD de combat). */
function muteIcon(muted: boolean): string {
  return icon(muted ? 'soundOff' : 'sound');
}

/** Une sauvegarde a-t-elle une progression à reprendre ? */
function hasProgress(ctx: ScreenCtx): boolean {
  const s = ctx.game.state;
  return (
    s.phase !== 'title' ||
    s.gold > 0 ||
    s.level > 1 ||
    s.day > 1 ||
    s.stats.runs > 0 ||
    s.inventory.length > 1
  );
}

export const mountTitle: ScreenMount = (container, ctx) => {
  const { game } = ctx;
  const el = document.createElement('div');
  el.className = 'screen screen--title';

  // Texte du sous-titre du Mode infini (A103) ; verrouillé avant achat (A112), il dit pourquoi.
  const endlessSub = `Niveau ${ENDLESS_START.level} · arbre complet · ${ENDLESS_START.diceCount} d${ENDLESS_START.dieSides} ${RARITY_NAME[ENDLESS_START.dieRarity]}s Corrupted${ENDLESS_START.dieLevel > 1 ? ` niveau ${ENDLESS_START.dieLevel}` : ''}`;
  const continuing = hasProgress(ctx);
  const s = game.state;
  // DECISIONS §E (A8) : une sauvegarde d'une version antérieure est effacée au
  // chargement. Le message n'est affiché **qu'une fois** (il est consommé ici).
  const wasReset = consumeSaveResetNotice();

  // v0.16.0 (refonte F1, lot 3) : emblème (dé facetté à liseré d'or), titre en
  // Cinzel sur fond d'ardoise facetté (CSS seule), boutons de pierre, icônes au
  // lieu des émojis. Le bouton de son suit la convention du HUD :
  // `aria-pressed="true"` = son actif.
  el.innerHTML = `
    <div class="title-top">
      <button type="button" class="f-ibtn title-mute" data-action="mute" aria-label="Son" aria-pressed="${
        isMuted() ? 'false' : 'true'
      }">${muteIcon(isMuted())}</button>
    </div>
    <div class="title-center">
      <div class="title-emblem">${dieArt(12, 'legendary', { size: 92, stroke: 2.4 })}</div>
      <h1 class="title-logo">DiceOrDie&nbsp;!</h1>
      <div class="f-rule title-rule" aria-hidden="true"></div>
      <p class="title-tagline">Lancez vos dés. Survivez à l'Invasion.</p>
      ${
        wasReset
          ? `<p class="f-notice title-notice" role="status">${icon('revive')}<span data-role="save-reset">Nouvelle version : la partie a été réinitialisée.</span></p>`
          : ''
      }
    </div>
    <div class="title-actions">
      <button type="button" class="f-btn f-btn--primary f-btn--block title-play" data-action="play">
        ${icon('play')}<span>${continuing ? 'Continuer' : 'Jouer'}</span>
      </button>
      ${
        continuing
          ? `<p class="title-summary num">Niveau ${s.level} · Jour ${Math.min(s.day, 5)}/5 · ${formatGold(s.gold)} or</p>`
          : ''
      }
      <button type="button" class="f-btn f-btn--ghost f-btn--block" data-action="new-game" aria-expanded="false" aria-controls="title-new-game">Nouvelle partie</button>
      <div class="f-panel title-newgame" id="title-new-game" data-role="new-game-choice" hidden>
        <p class="title-newgame-text">Efface la partie en cours (arbre, dés, or) — irréversible. Commencer en :</p>
        <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="new-campaign">
          <span class="title-newgame-label">Campagne</span>
          <span class="title-newgame-sub num">Niveau 1 · Dé de l'aïeul</span>
        </button>
        <span class="title-lockwrap" data-role="endless-wrap">
          <button type="button" class="f-btn f-btn--block" data-action="new-endless">
            <span class="title-newgame-label">Mode infini</span>
            <span class="title-newgame-sub num" data-role="endless-sub">${endlessSub}</span>
          </button>
          <span class="title-lockhit" data-role="endless-locked-hit" hidden></span>
        </span>
        <button type="button" class="f-btn f-btn--ghost f-btn--sm f-btn--block" data-action="new-cancel">Annuler</button>
      </div>
      <button type="button" class="f-btn f-btn--ghost f-btn--sm title-leaderboard" data-action="leaderboard">${icon('trophy')}<span>Classements</span></button>
    </div>
    <div class="title-footer">
      <button type="button" class="f-btn f-btn--ghost f-btn--sm title-dev" data-action="dev">${icon('book')}<span>Notes de dev</span></button>
      <a class="title-version" data-action="privacy" href="./confidentialite.html" target="_blank" rel="noopener" style="padding:12px 6px;font-size:12.5px;text-decoration:underline;text-underline-offset:3px">Confidentialité</a>
      <span class="title-version">${IS_PREPROD ? '<span class="f-badge" data-tone="danger">Préprod</span> ' : ''}v${APP_VERSION}</span>
    </div>
  `;

  container.appendChild(el);

  const muteBtn = el.querySelector<HTMLButtonElement>('[data-action="mute"]');
  const onMute = (): void => {
    const next = !isMuted();
    setMuted(next);
    ctx.battle.setMuted?.(next);
    if (muteBtn) {
      muteBtn.innerHTML = muteIcon(next);
      muteBtn.setAttribute('aria-pressed', next ? 'false' : 'true');
    }
  };
  muteBtn?.addEventListener('click', onMute);

  const playBtn = el.querySelector<HTMLButtonElement>('[data-action="play"]');
  const onPlay = (): void => {
    if (continuing) {
      // Rien à faire côté état : on démasque simplement l'écran déjà atteint
      // (Accalmie la plupart du temps — les phases de combat ne sont pas
      // sauvegardées, voir DECISIONS D8).
      ctx.navigate(game.state.phase);
    } else {
      // Lore (COMMENTS v0.8.0 §1.1, DECISIONS §G A33) : à la toute première
      // Expédition de la partie, un encart plein écran précède le départ —
      // `startRun`/`startExpedition` ne sont appelés qu'à sa fermeture, le
      // chrono ne tourne donc pas pendant la lecture.
      maybeShowLore(ctx, () => {
        game.startRun();
        game.startExpedition();
      });
    }
  };
  playBtn?.addEventListener('click', onPlay);

  // v0.53.0 (A103) : « Nouvelle partie » déplie un choix — **Campagne** (niveau
  // 1, Dé de l'aïeul, comme avant) ou **Mode infini** (niveau 11, arbre de
  // campagne complet, sept d20 Inhabituels Corrupted de niveau 1 (A106),
  // `core/endlessStart.ts`). Le panneau tient lieu de confirmation : plus de
  // `window.confirm`. Dans les deux cas la sauvegarde est remplacée puis la
  // page rechargée (le `Game` de la coquille ne se remplace pas à chaud).
  const newGameBtn = el.querySelector<HTMLButtonElement>('[data-action="new-game"]');
  const choice = el.querySelector<HTMLElement>('[data-role="new-game-choice"]');
  const campaignBtn = el.querySelector<HTMLButtonElement>('[data-action="new-campaign"]');
  const endlessBtn = el.querySelector<HTMLButtonElement>('[data-action="new-endless"]');
  const cancelBtn = el.querySelector<HTMLButtonElement>('[data-action="new-cancel"]');
  const showChoice = (open: boolean): void => {
    if (choice) choice.hidden = !open;
    newGameBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) campaignBtn?.focus({ preventScroll: true });
    else newGameBtn?.focus({ preventScroll: true });
  };
  const onNewGame = (): void => showChoice(!!choice?.hidden);
  const onCancel = (): void => showChoice(false);
  const onCampaign = (): void => {
    clearSave();
    window.location.reload();
  };
  const onEndless = (): void => {
    clearSave();
    // Sans stockage, la partie rechargée sera une campagne neuve : rien à faire de plus.
    writeSave(endlessStartSave());
    window.location.reload();
  };
  // A112 : le Mode infini est verrouillé avant achat. Un bouton désactivé ne reçoit plus de toucher :
  // une pastille transparente le couvre et ouvre l'explication. Suit l'état de l'appareil (`entitlement`).
  const endlessSubEl = el.querySelector<HTMLElement>('[data-role="endless-sub"]');
  const endlessHit = el.querySelector<HTMLElement>('[data-role="endless-locked-hit"]');
  const applyEndlessLock = (): void => {
    const locked = hasGateApi(game) && !isEntitled(game);
    if (endlessBtn) {
      endlessBtn.disabled = locked;
      endlessBtn.setAttribute('aria-disabled', String(locked));
    }
    if (endlessHit) endlessHit.hidden = !locked;
    if (endlessSubEl) endlessSubEl.innerHTML = locked ? `${icon('lock')}Version complète requise` : endlessSub;
  };
  const onLockedPress = (): void => toast('Terminez la campagne ou débloquez le jeu complet.', 3200, 'lock');
  endlessHit?.addEventListener('pointerdown', onLockedPress);
  applyEndlessLock();
  const offEntitlement = onGameEvent(game, 'entitlement', applyEndlessLock);

  newGameBtn?.addEventListener('click', onNewGame);
  cancelBtn?.addEventListener('click', onCancel);
  campaignBtn?.addEventListener('click', onCampaign);
  endlessBtn?.addEventListener('click', onEndless);

  const devBtn = el.querySelector<HTMLButtonElement>('[data-action="dev"]');
  const onDev = (): void => openDevNotes(ctx);
  devBtn?.addEventListener('click', onDev);

  const leaderboardBtn = el.querySelector<HTMLButtonElement>('[data-action="leaderboard"]');
  const onLeaderboard = (): void => openLeaderboard();
  leaderboardBtn?.addEventListener('click', onLeaderboard);

  return () => {
    offEntitlement();
    endlessHit?.removeEventListener('pointerdown', onLockedPress);
    leaderboardBtn?.removeEventListener('click', onLeaderboard);
    devBtn?.removeEventListener('click', onDev);
    muteBtn?.removeEventListener('click', onMute);
    playBtn?.removeEventListener('click', onPlay);
    newGameBtn?.removeEventListener('click', onNewGame);
    cancelBtn?.removeEventListener('click', onCancel);
    campaignBtn?.removeEventListener('click', onCampaign);
    endlessBtn?.removeEventListener('click', onEndless);
  };
};
